import { apiBase } from "./apiHost";

/*
 * Where this app talks to.
 *
 * The two BRIDGE bases — auth/native learning, and the community endpoints —
 * are no longer constants here. Which bridge a membership uses is decided at
 * boot by apiHost.js, because the Lumen -> Laravel cutover runs per owner and
 * this app cannot tell which side it is on without asking. Their defaults and
 * the per-environment fallbacks live there now; see API_URL below.
 *
 * Everything else in this file is still a plain value.
 */
const config = {
  /*
   * A GETTER, not a constant.
   *
   * Which bridge a membership talks to is decided at boot by apiHost.js (the
   * Lumen -> Laravel cutover runs per owner). Reading it at call time means the
   * 33 files that use `config.API_URL` get the right host without any of them
   * changing — they only ever read it as a property, never destructure it.
   *
   * An explicit REACT_APP_API_URL still wins: a developer pointing the app at
   * one host expects it to stay there.
   */
  get API_URL() {
    return process.env.REACT_APP_API_URL || apiBase();
  },

  VIRAL_SHARE_URL:
    process.env.NODE_ENV === 'production'
      ? 'https://fleximembership.flexifunnels.com' // Production URL
      : 'http://localhost:3000', // Local URL

  // Community backend (flexicommunities / flexi2.0) — group collection + join API.
  // A getter for the same reason as API_URL above.
  get COMMUNITY_API_URL() {
    return process.env.REACT_APP_COMMUNITY_API_URL || apiBase();
  },

  // Course-landing (public sales page) data lives on the membership ADMIN app
  // (fleximembership), NOT the Lumen bridge. Override with REACT_APP_LANDING_API_URL.
  LANDING_API_URL:
    process.env.REACT_APP_LANDING_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://fleximembership.flexifunnels.com/api'
      : 'http://flexiapi.local.com/api'),

  // The membership ADMIN app (fleximembership) — Laravel, not the Lumen bridge.
  // Email notifications are raised here: it owns the one model / job / controller
  // and dispatches the send job. The bridge has no queue/job support, which is why
  // this call goes direct. Override with REACT_APP_MEMBERSHIP_API_URL.
  MEMBERSHIP_API_URL:
    process.env.REACT_APP_MEMBERSHIP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://fleximembership.flexifunnels.com/api'
      : 'http://fleximembership.local.com/api'),

  // Shared media service for post/comment image uploads. The membership API used
  // to take the files itself and write them under its own /storage/posts, which
  // is what broke in production; images now go straight here and the post request
  // carries the returned URLs. Override with REACT_APP_MEDIA_API_URL.
  // Contract: POST {MEDIA_API_URL}/upload, multipart, with community_token +
  // community_member_id + the file -> { error, kind, url, urls[], files[] }.
  // Field per kind: images[] (a list — a post can carry several), and the
  // singular video / audio / file for the kinds that are one per post. An
  // unrecognised field answers 422 "No file provided". The service reads the
  // KIND from the MIME type and files uploads under frontend_image/,
  // frontend_video/, frontend_audio/ or frontend_doc/. Documents are accepted
  // as of its August update: pdf, doc, docx, txt, csv, ppt, pptx, xls, xlsx,
  // zip, rar.
  MEDIA_API_URL: process.env.REACT_APP_MEDIA_API_URL || 'https://mediaapi.flexifunnels.com',

  // GIPHY key for the post/comment GIF picker. Set REACT_APP_GIPHY_KEY in .env
  // for production; falls back to GIPHY's public demo key for local dev.
  GIPHY_KEY: process.env.REACT_APP_GIPHY_KEY || 'GlVGYHkr3WSBnllca54iNt0yFbjz7L65',

  // FlexiFunnels image microservice — the profile photo goes straight here from
  // the browser, the same upload the legacy learner app used (Course_Membership
  // src/pages/profile.js). It is a FlexiFunnels-wide service with no local
  // equivalent, so there is no per-environment default to fall back to: point
  // REACT_APP_IMAGE_CDN_URL at a sandbox if one exists, otherwise local uploads
  // hit production. See uploadImage.js.
  IMAGE_CDN_URL: process.env.REACT_APP_IMAGE_CDN_URL || 'https://img.myflxfnls.com',
};

export default config;